/**
 * Chart Colors Utility
 * 
 * Centralized chart color management using CSS variables for theme consistency.
 * All chart components should use these utilities instead of hardcoded hex values.
 */

// Primary chart color palette using CSS variables
// These automatically adapt to light/dark mode
export const CHART_COLORS = [
  'var(--chart-1)', // Orange — first series
  'var(--chart-2)', // Burnt orange
  'var(--chart-3)', // Deep orange
  'var(--chart-4)', // Rust
  'var(--chart-5)', // Deepest — bottom-of-funnel counts
  'var(--chart-revenue)', // Revenue (aliases chart-3)
] as const

// Fallback hex colors for contexts where CSS variables aren't available (e.g., SVG exports)
// These should match the CSS variable values in light mode
export const CHART_COLORS_HEX = {
  light: [
    '#F56600', // chart-1: oklch(0.68 0.2 48)
    '#D53E00', // chart-2: oklch(0.58 0.2 41)
    '#B22900', // chart-3: oklch(0.5 0.18 37)
    '#892504', // chart-4: oklch(0.42 0.14 36)
    '#642211', // chart-5: oklch(0.35 0.1 35)
    '#B22900', // chart-revenue: = chart-3
  ],
  dark: [
    '#FF9044', // chart-1: oklch(0.76 0.16 52)
    '#F66400', // chart-2: oklch(0.68 0.2 46)
    '#DC4500', // chart-3: oklch(0.6 0.2 41)
    '#B53700', // chart-4: oklch(0.52 0.17 38)
    '#932F11', // chart-5: oklch(0.45 0.14 36)
    '#DC4500', // chart-revenue: = chart-3
  ],
} as const

/**
 * Get a chart color by index with automatic cycling
 * @param index - The index of the color to retrieve
 * @returns CSS variable string for the color
 */
export function getChartColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length]
}

/**
 * Get chart colors for a specific number of data points
 * @param count - Number of colors needed
 * @returns Array of CSS variable strings
 */
export function getChartColors(count: number): string[] {
  return Array.from({ length: count }, (_, i) => getChartColor(i))
}

/**
 * Get hex color for contexts where CSS variables aren't supported
 * @param index - The index of the color to retrieve
 * @param theme - 'light' or 'dark' theme
 * @returns Hex color string
 */
export function getChartColorHex(
  index: number,
  theme: 'light' | 'dark' = 'light'
): string {
  const colors = CHART_COLORS_HEX[theme]
  return colors[index % colors.length]
}

/**
 * Chart color configuration for Recharts components
 */
export const chartColorConfig = {
  visitors: {
    label: 'Unique Visitors',
    color: 'var(--chart-2)',
  },
  revenue: {
    label: 'Revenue',
    color: 'var(--chart-revenue)',
  },
  sessions: {
    label: 'Unique Visitors',
    color: 'var(--chart-2)',
  },
  views: {
    label: 'Total Views',
    color: 'var(--chart-1)',
  },
  clicks: {
    label: 'Clicks',
    color: 'var(--chart-1)',
  },
  conversions: {
    label: 'Conversions',
    color: 'var(--chart-5)',
  },
} as const

/**
 * Semantic colors for specific data types
 */
export const semanticChartColors = {
  primary: 'var(--chart-1)',
  secondary: 'var(--chart-2)',
  tertiary: 'var(--chart-3)',
  highlight: 'var(--chart-4)',
  accent: 'var(--chart-5)',
  revenue: 'var(--chart-revenue)',
  positive: 'var(--foreground)', // Delta up: ink (White signal v2), never a series
  negative: 'var(--muted-foreground)', // Delta down: muted ink, never a series
  neutral: 'var(--muted-foreground)',
} as const

/**
 * Experiment variant color. Control is the neutral baseline; variants walk the
 * ramp from chart-1 in order (B = chart-1, C = chart-2, …). Assumes control is
 * listed first, as the experiments API returns it.
 */
export function getVariantColor(index: number, isControl?: boolean): string {
  if (isControl) return 'var(--muted-foreground)'
  return `var(--chart-${(Math.max(index - 1, 0) % 5) + 1})`
}

export type ChartColorKey = keyof typeof chartColorConfig
export type SemanticColorKey = keyof typeof semanticChartColors
